import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { ChevronDown, ChevronRight, Check, X as XIcon, Loader2, Sparkles, RefreshCw, PlusCircle, ShieldAlert, Wand2 } from 'lucide-react';
import { flattenTree, chipClass } from './categoryUtils';
import { HitChips } from './LibraryWordingDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLE = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  skipped: 'bg-slate-100 text-slate-500 border-slate-200',
};

export const ImportItemRow = ({ sessionId, item, categories, onItemChanged }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null); // lazily seeded on expand
  const [busy, setBusy] = useState(false);
  const [dupWarn, setDupWarn] = useState(null);
  const [scrub, setScrub] = useState(item.scrub || null); // latest server-side sensitive-content check
  const tree = flattenTree(categories);
  const catMap = Object.fromEntries(categories.map(c => [c.id, c]));
  const isPending = item.status === 'pending';
  const flagged = isPending && !!scrub?.hits?.length && !item.scrub_dismissed;

  const expand = () => {
    if (!open && !form) {
      setForm({
        title: item.title, body: item.body,
        category_ids: item.category_ids || [],
        keywords: item.keywords || [],
      });
    }
    setOpen(o => !o);
  };

  const act = async (kind, confirmDup = false, confirmScrub = false) => {
    setBusy(true);
    try {
      const url = `${API_URL}/api/rfp/import/${sessionId}/items/${item.idx}/${kind}`;
      const payload = kind === 'accept' && form
        ? { ...form, ...(confirmDup ? { confirm_duplicate: true } : {}), ...(confirmScrub ? { confirm_scrub: true } : {}) } : {};
      const res = await axios.post(url, payload, { withCredentials: true });
      toast.success(kind === 'accept'
        ? `"${res.data.title || item.title}" ${item.action === 'update' ? 'updated in' : 'added to'} the library`
        : 'Capability skipped');
      setDupWarn(null);
      setOpen(false);
      onItemChanged();
    } catch (err) {
      if (err?.response?.status === 409) {
        const d = err.response.data?.detail;
        if (d?.code === 'sensitive') {
          setScrub(d.scrub);
          toast.warning(d.message);
        } else {
          setDupWarn(typeof d === 'string' ? { message: d } : (d || { message: 'Possible duplicate of an existing capability' }));
        }
      } else {
        toast.error(err?.response?.data?.detail || `Could not ${kind} the capability`);
      }
    } finally {
      setBusy(false);
    }
  };

  const useGeneric = () => {
    setForm(f => ({ ...f, body: scrub.proposed_body }));
    toast.success('Generic wording applied — review, then accept');
  };

  const toggleCat = (id) => setForm(f => ({
    ...f,
    category_ids: f.category_ids.includes(id) ? f.category_ids.filter(x => x !== id) : [...f.category_ids, id],
  }));

  const catNames = (item.category_ids || []).map(id => catMap[id]?.name).filter(Boolean);

  return (
    <div className="border border-slate-200 rounded-lg" data-testid={`import-item-${item.idx}`}>
      <button onClick={expand} className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50 rounded-lg"
        data-testid={`import-item-toggle-${item.idx}`}>
        {open ? <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
        <span className="text-xs font-semibold text-[#0F2040] truncate flex-1">{item.title}</span>
        {item.action === 'update' ? (
          <Badge variant="outline" className="text-[9px] bg-sky-50 text-sky-700 border-sky-200 shrink-0" title={`Updates: ${item.target_title}`}>
            <RefreshCw className="w-2.5 h-2.5 mr-1" />Update existing
          </Badge>
        ) : (
          <Badge variant="outline" className="text-[9px] bg-violet-50 text-violet-700 border-violet-200 shrink-0">
            <PlusCircle className="w-2.5 h-2.5 mr-1" />Create new
          </Badge>
        )}
        {catNames.length > 0 ? catNames.slice(0, 2).map(n => (
          <Badge key={n} variant="outline" className={`text-[9px] shrink-0 ${chipClass(n)}`}>{n}</Badge>
        )) : item.recommended_category ? (
          <Badge variant="outline" className="text-[9px] bg-rose-50 text-rose-700 border-rose-200 shrink-0"
            title="No existing category fits — a new one will be created on accept">
            Unmatched → {item.recommended_category.name} ({item.recommended_category.shortname})
          </Badge>
        ) : null}
        {flagged && (
          <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-800 border-amber-300 shrink-0" data-testid={`import-item-scrub-chip-${item.idx}`}
            title={scrub.hits.map(h => h.name).join(', ')}>
            <ShieldAlert className="w-2.5 h-2.5 mr-1" />Contains client name / sensitive terms
          </Badge>
        )}
        <Badge variant="outline" className={`text-[9px] shrink-0 capitalize ${STATUS_STYLE[item.status] || ''}`}
          data-testid={`import-item-status-${item.idx}`}>
          {item.status === 'pending' ? 'Pending review' : item.status}
        </Badge>
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-2 border-t border-slate-100 pt-2">
          {item.action === 'update' && (
            <p className="text-[10px] text-sky-700 bg-sky-50 border border-sky-100 rounded px-2 py-1">
              Accepting replaces the body of <span className="font-semibold">"{item.target_title}"</span> with this wording (keywords regenerate).
            </p>
          )}
          {isPending && form ? (
            <>
              {flagged && (
                <div className="text-[10px] bg-amber-50 border border-amber-200 rounded px-2 py-1.5 space-y-1.5" data-testid={`import-item-scrub-panel-${item.idx}`}>
                  <div className="flex items-start gap-2">
                    <ShieldAlert className="w-3.5 h-3.5 text-amber-700 shrink-0 mt-0.5" />
                    <span className="flex-1 text-amber-800">
                      <span className="font-semibold">Sensitive content detected.</span> The library must stay client-agnostic — use the generic wording, edit the text below, or accept anyway if this is a false positive.
                      {scrub.hits.some(h => h.kind === 'pricing') && <span className="block text-rose-700 mt-0.5">Pricing figures aren't rewritten automatically — remove or generalise them by hand.</span>}
                    </span>
                  </div>
                  <HitChips hits={scrub.hits} testId={`import-item-scrub-hits-${item.idx}`} />
                  <div className="flex flex-wrap gap-1.5 justify-end">
                    {scrub.proposed_body && (
                      <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                        onClick={useGeneric} disabled={busy} data-testid={`import-item-use-generic-${item.idx}`}>
                        <Wand2 className="w-3 h-3 mr-1" />Use generic wording
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 border-amber-300 text-amber-800 hover:bg-amber-100"
                      onClick={() => act('accept', false, true)} disabled={busy} data-testid={`import-item-accept-anyway-scrub-${item.idx}`}>
                      Accept anyway (false positive)
                    </Button>
                  </div>
                </div>
              )}
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                className="h-8 text-xs" data-testid={`import-item-title-${item.idx}`} />
              <Textarea value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                rows={8} className="text-xs font-mono" data-testid={`import-item-body-${item.idx}`} />
              <div className="grid grid-cols-2 gap-x-3 border border-slate-200 rounded-md p-2 max-h-28 overflow-y-auto">
                {tree.map(c => (
                  <label key={c.id} className="flex items-center gap-1.5 text-[10px] text-slate-700 cursor-pointer py-0.5"
                    style={{ paddingLeft: `${c.depth * 12}px` }}>
                    <input type="checkbox" className="accent-[#0F2040]" checked={form.category_ids.includes(c.id)}
                      onChange={() => toggleCat(c.id)} data-testid={`import-item-cat-${item.idx}-${c.id}`} />
                    {c.name}
                  </label>
                ))}
              </div>
              {form.category_ids.length === 0 && item.recommended_category && (
                <p className="text-[10px] text-rose-700">
                  No category ticked — accepting will auto-create <span className="font-semibold">
                  {item.recommended_category.name} ({item.recommended_category.shortname})</span> and assign it.
                </p>
              )}
              <div className="flex flex-wrap gap-1">
                {(form.keywords || []).map(k => (
                  <span key={k} className="text-[9px] bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5 flex items-center gap-1">
                    <Sparkles className="w-2.5 h-2.5 text-[#4ABFCF]" />{k}
                  </span>
                ))}
              </div>
              {dupWarn && (
                <div className="text-[10px] bg-amber-50 border border-amber-200 rounded px-2 py-1.5 space-y-1.5"
                  data-testid={`import-item-dup-warn-${item.idx}`}>
                  <div className="flex items-start gap-2">
                    <span className="flex-1 text-amber-800 font-semibold">
                      {dupWarn.message} — compare below, then skip this item or accept it regardless.
                    </span>
                    <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 border-amber-300 text-amber-800 hover:bg-amber-100 shrink-0"
                      onClick={() => act('accept', true)} disabled={busy}
                      data-testid={`import-item-accept-anyway-${item.idx}`}>Accept anyway</Button>
                  </div>
                  {dupWarn.match && (
                    <div className="grid grid-cols-2 gap-2" data-testid={`import-item-dup-diff-${item.idx}`}>
                      <div className="bg-white border border-amber-100 rounded p-1.5">
                        <p className="font-semibold text-slate-700 mb-0.5 truncate"
                          title={dupWarn.match.title}>Existing · {dupWarn.match.capability_id} — {dupWarn.match.title}</p>
                        <p className="text-slate-600 whitespace-pre-wrap max-h-32 overflow-y-auto">{dupWarn.match.body}</p>
                      </div>
                      <div className="bg-white border border-amber-100 rounded p-1.5">
                        <p className="font-semibold text-slate-700 mb-0.5 truncate">Incoming — {(form?.title || item.title)}</p>
                        <p className="text-slate-600 whitespace-pre-wrap max-h-32 overflow-y-auto">{form?.body || item.body}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="flex justify-end gap-2 pt-1">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => act('skip')} disabled={busy}
                  data-testid={`import-item-skip-${item.idx}`}>
                  <XIcon className="w-3 h-3 mr-1" />Skip
                </Button>
                <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => act('accept')} disabled={busy} data-testid={`import-item-accept-${item.idx}`}>
                  {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
                  Accept into library
                </Button>
              </div>
            </>
          ) : (
            <p className="text-xs text-slate-500 whitespace-pre-line max-h-40 overflow-y-auto">{item.body}</p>
          )}
        </div>
      )}
    </div>
  );
};
